import React, { useEffect, useState } from "react";
import { Seo } from "../components/Seo";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { auth } from "../lib/api";
import { TotalReachLogo } from "../components/Logo";
import { Loader2, CheckCircle2, ArrowRight, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Welcome() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling"); // polling | paid | failed
  const [planName, setPlanName] = useState(null);
  const [err, setErr] = useState(null);
  const sessionId = params.get("sub_session");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); setErr("Missing checkout session ID."); return; }
    let stopped = false;
    let attempts = 0;
    const tick = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/api/public/subscriptions/status/${sessionId}`);
        if (data.plan_key) setPlanName(data.plan_key);
        if (data.payment_status === "paid" && data.token && data.user) {
          auth.login(data.token, data.user);
          setStatus("paid");
          // Small delay so the user sees the success screen before we push
          // them into the dashboard.
          setTimeout(() => { window.location.href = "/onboarding/checklist"; }, 1500);
          return;
        }
      } catch (e) {
        // 404 could mean Stripe hasn't posted yet; retry a bunch of times
        // before giving up.
        if (attempts > 24) { // ~2 minutes at 5s
          setStatus("failed");
          setErr(e?.response?.data?.detail || "We couldn't confirm your payment. Contact info@totalreach.us with your receipt.");
          return;
        }
      }
      if (!stopped) setTimeout(tick, 5000);
    };
    tick();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-app text-white flex flex-col" data-testid="welcome-page">
      <Seo title="Welcome" noindex />
      <nav className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-center">
          <TotalReachLogo size={22} />
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center px-6">
        {status === "polling" && (
          <div className="text-center max-w-md" data-testid="welcome-polling">
            <Loader2 className="w-10 h-10 animate-spin text-cyan-300 mx-auto" />
            <h1 className="font-display text-3xl font-bold mt-6">Confirming your payment…</h1>
            <p className="text-slate-400 mt-3 text-sm">This normally takes a few seconds. Don't close this window — we'll drop you into your workspace as soon as Stripe confirms.</p>
            {planName && <div className="mt-4 text-xs text-cyan-300 uppercase tracking-widest">Plan · {planName.replace(/_/g, " ")}</div>}
          </div>
        )}
        {status === "paid" && (
          <div className="text-center max-w-md" data-testid="welcome-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <h1 className="font-display text-4xl font-bold mt-6">Welcome to TotalReach.</h1>
            <p className="text-slate-300 mt-3">Your subscription is active. Setting up your workspace…</p>
            <button onClick={() => { window.location.href = "/dashboard"; }}
              data-testid="welcome-continue-btn"
              className="mt-6 rounded-full px-6 py-3 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold inline-flex items-center gap-2">
              Go to your dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {status === "failed" && (
          <div className="text-center max-w-md" data-testid="welcome-failed">
            <ShieldAlert className="w-14 h-14 text-rose-400 mx-auto" />
            <h1 className="font-display text-3xl font-bold mt-6">We couldn't confirm your payment.</h1>
            <p className="text-slate-400 mt-3 text-sm">{err || "Please refresh, or email info@totalreach.us with your receipt and we'll sort it out."}</p>
            <div className="mt-6 flex justify-center gap-3">
              <button onClick={() => navigate("/pricing")}
                data-testid="welcome-retry-btn"
                className="rounded-full px-6 py-2.5 border border-white/15 text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300 text-sm">
                Back to pricing
              </button>
              <a href="mailto:info@totalreach.us"
                className="rounded-full px-6 py-2.5 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold text-sm inline-flex items-center gap-1.5">
                Email support
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
